"use client";

import useItem from "hooks/useItem";
import useList from "hooks/useList";
import AlertsContext from "providers/AlertProvider";
import { useContext, useEffect, useCallback, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { IconNames } from "components/Icon/Icon";
import useRouter from "common/hooks/use-router";
import { Sidebar } from "primereact/sidebar";
import clsx from "clsx";
import CrudView from "./components/CrudView";
import type { CardActions, CardGridColumns } from "./components/CrudCards";
import type { CrudHeaderAction } from "./components/CrudHeader";
import type { TableColumn } from "../DataTable/DataTable";
import type { CrudFilter } from "./components/CrudFilters";
import SelectionActionBar from "../DataTable/components/SelectionActionBar";
import { type CrudTab } from "./components/CrudTabs";

type FormMode = "page" | "sidebar" | "modal";

type Props = {
  collectionName: string;
  columns?: TableColumn[];
  headerActions?: CrudHeaderAction[];
  filters?: CrudFilter[];
  defaultFilters?: any;
  defaultSort?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  canSeeDetails?: boolean;
  icon?: IconNames;
  onEdit?: (id: any) => void;
  onDelete?: (id: any) => void;
  onNavigate?: (id: any) => void;
  formMode?: FormMode;
  SidebarFormComponent?: (props: any) => JSX.Element;
  sidebarPosition?: "left" | "right" | "top" | "bottom";
  sidebarClassName?: string;
  actionsHeader?: string;
  hideBackgroundHeader?: boolean;
  colorMode?: "light" | "dark";
  compact?: boolean;
  className?: string;
  rowExpansionTemplate?: (rowData: any) => React.ReactNode;
  rowExpandable?: (rowData: any) => boolean;
  mode?: "table" | "cards";
  renderCard?: (item: any, actions: CardActions) => React.ReactNode;
  gridColumns?: CardGridColumns;
  hideHeader?: boolean;
  tabs?: CrudTab[];
  tabsFilterKey?: string;
  onFiltersChange?: (filters: Record<string, any>) => void;
  nameSpace?: string;
};

function CrudList({
  collectionName,
  columns = [],
  headerActions = [],
  filters = [],
  defaultFilters,
  defaultSort,
  canEdit = true,
  canDelete = true,
  canSeeDetails = true,
  icon,
  onEdit,
  onDelete,
  onNavigate,
  formMode = "page",
  SidebarFormComponent,
  sidebarPosition = "right",
  sidebarClassName = "w-full md:w-2/3 lg:w-1/2",
  actionsHeader,
  hideBackgroundHeader = true,
  compact = false,
  colorMode = "dark",
  className,
  rowExpansionTemplate,
  rowExpandable,
  mode = "table",
  renderCard,
  gridColumns,
  hideHeader = false,
  tabs,
  tabsFilterKey,
  onFiltersChange,
  nameSpace,
}: Props) {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const { navigate } = useRouter();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [itemIdToEdit, setItemIdToEdit] = useState<number | null>(null);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>(
    tabs?.[0]?.value ?? null,
  );
  const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);

  const openSidebar = useCallback((id: number | null) => {
    setItemIdToEdit(id);
    setIsSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
    setItemIdToEdit(null);
  }, []);

  const {
    fetchItems,
    items,
    isFetching,
    filters: activeFilters,
    setFilter,
    sort,
    setSort,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<any>(collectionName, {
    defaultSort: defaultSort ?? "createdAt:DESC",
    defaultFilters: defaultFilters ?? {},
    cachePrefix: collectionName,
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const { removeItem } = useItem<any>(collectionName, "");

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
        setAlert(t("common.deleted"), "success");
      } catch (e: any) {
        setAlert(e, "danger");
        return e;
      }
      onDelete?.(id);
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert, onDelete, t],
  );

  const handleBulkDelete = useCallback(async () => {
    if (selectedItems.length === 0) return;

    setIsDeletingMultiple(true);
    try {
      // Delete all selected items in parallel
      await Promise.all(
        selectedItems.map(async (item) => {
          const { id } = item;
          removeListItem(id);
          await removeItem(id);
          onDelete?.(id);
        }),
      );

      setAlert(
        t("actions.deleteMultipleSuccess", { count: selectedItems.length }),
        "success",
      );
      setSelectedItems([]);
      fetchItems();
    } catch (e: any) {
      setAlert(e, "danger");
    } finally {
      setIsDeletingMultiple(false);
    }
  }, [
    selectedItems,
    removeListItem,
    removeItem,
    onDelete,
    fetchItems,
    setAlert,
    t,
  ]);

  const isMounted = useRef(false);
  useEffect(() => {
    if (!onFiltersChange) return;
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onFiltersChange(activeFilters);
  }, [activeFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = useCallback(
    (value: string | null) => {
      setActiveTab(value);
      if (tabsFilterKey) {
        setFilter(tabsFilterKey, (value ?? undefined) as any);
      }
    },
    [tabsFilterKey, setFilter],
  );

  const handleEdit = useCallback(
    (id: any) => {
      if (onEdit) {
        onEdit(id);
      } else if (formMode === "sidebar") {
        openSidebar(id);
      } else {
        navigate(`/${collectionName}/edit/${id}`);
      }
    },
    [onEdit, formMode, openSidebar, navigate, collectionName],
  );

  const translatedHeaderActions = headerActions.map((action) => {
    const { action: actionType } = action;
    if (typeof actionType === "string") {
      if (actionType === "openSidebar") {
        return {
          ...action,
          action: () => openSidebar(null),
        };
      }
    }

    return action;
  });

  return (
    <div
      className={clsx(
        "mx-auto max-w-full relative flex-1 h-full text-slate-500 overflow-y-hidden",
        className,
      )}
    >
      {!hideBackgroundHeader && (
        <div
          className="h-[500px] lg:h-[250px] w-full absolute top-0 left-0 flex bg-cover bg-center"
          style={{ backgroundImage: "url(/images/header-background.png)" }}
        />
      )}
      <div className={clsx("flex-1", !compact && "px-4")}>
        <CrudView
          title={t(`${nameSpace ?? collectionName}.listTitle`)}
          subtitle={t(`${nameSpace ?? collectionName}.listSubtitle`)}
          icon={icon}
          headerActions={translatedHeaderActions}
          data={items ?? []}
          columns={columns}
          isFetching={isFetching}
          pageCount={pageCount}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          itemsCount={itemsCount}
          itemsPerPage={itemsPerPage}
          sort={sort}
          setSort={setSort}
          onDelete={canDelete ? handleDelete : undefined}
          onEdit={canEdit ? handleEdit : undefined}
          onRowClick={
            canSeeDetails
              ? onNavigate || handleEdit
              : canEdit
              ? handleEdit
              : undefined
          }
          filters={filters}
          activeFilters={activeFilters}
          setFilter={setFilter}
          emptyMessage={t(`${collectionName}.noItems`)}
          actionsHeader={actionsHeader}
          colorMode={colorMode}
          selectionMode="checkbox"
          selection={selectedItems}
          compact={compact}
          onSelectionChange={setSelectedItems}
          rowExpansionTemplate={rowExpansionTemplate}
          rowExpandable={rowExpandable}
          mode={mode}
          renderCard={renderCard}
          gridColumns={gridColumns}
          header={hideHeader ? null : undefined}
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <SelectionActionBar
          selectedCount={selectedItems.length}
          onDelete={canDelete ? handleBulkDelete : undefined}
          onClearSelection={() => setSelectedItems([])}
          isDeleting={isDeletingMultiple}
        />
      </div>
      {formMode && SidebarFormComponent && (
        <Sidebar
          visible={isSidebarOpen}
          onHide={closeSidebar}
          position={sidebarPosition}
          className={sidebarClassName}
        >
          <SidebarFormComponent
            id={itemIdToEdit}
            onBack={closeSidebar}
            onSubmitSuccess={fetchItems}
          />
        </Sidebar>
      )}
    </div>
  );
}

export default CrudList;
